import { useCallback, useRef, type ReactNode } from "react";
import GridLayout, { WidthProvider } from "react-grid-layout/legacy";
import type { Layout } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import type { GridCompactType } from "../types";
import {
  DEFAULT_GRID_COLS,
  DEFAULT_ROW_HEIGHT,
  DEFAULT_GRID_MARGIN,
} from "../types";

function layoutsEqual(a: Layout, b: Layout): boolean {
  if (a.length !== b.length) return false;
  const bMap = new Map(b.map((item) => [item.i, item]));
  return a.every((ai) => {
    const bi = bMap.get(ai.i);
    return bi !== undefined && ai.x === bi.x && ai.y === bi.y && ai.w === bi.w && ai.h === bi.h;
  });
}

const GridLayoutWithWidth = WidthProvider(GridLayout);

export interface ReactGridShellProps {
  /** Current layout (from DashboardContext or controlled). */
  layout: Layout;
  /** Called when the user drags or resizes items. */
  onLayoutChange: (layout: Layout) => void;
  /** Render a widget by id. */
  renderItem: (id: string) => ReactNode;
  cols?: number;
  rowHeight?: number;
  margin?: [number, number];
  containerPadding?: [number, number];
  compactType?: GridCompactType;
  preventCollision?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function ReactGridShell({
  layout,
  onLayoutChange,
  renderItem,
  cols = DEFAULT_GRID_COLS,
  rowHeight = DEFAULT_ROW_HEIGHT,
  margin = DEFAULT_GRID_MARGIN,
  containerPadding = DEFAULT_GRID_MARGIN,
  compactType = "vertical",
  preventCollision = false,
  isDraggable = true,
  isResizable = true,
  className = "layout",
  style,
}: ReactGridShellProps) {
  // Track whether user is actively dragging or resizing.
  // During interaction, skip intermediate onLayoutChange calls to avoid
  // re-rendering the entire widget tree on every animation frame.
  const interactingRef = useRef(false);

  const handleDragStart = useCallback(() => {
    interactingRef.current = true;
  }, []);

  const handleDragStop = useCallback((newLayout: Layout) => {
    interactingRef.current = false;
    onLayoutChange(newLayout);
  }, [onLayoutChange]);

  const handleResizeStart = useCallback(() => {
    interactingRef.current = true;
  }, []);

  const handleResizeStop = useCallback((newLayout: Layout) => {
    interactingRef.current = false;
    onLayoutChange(newLayout);
  }, [onLayoutChange]);

  const handleLayoutChange = useCallback((newLayout: Layout) => {
    if (interactingRef.current) return;
    // react-grid-layout fires onLayoutChange on mount and whenever the layout
    // prop changes, even without user interaction. Skip these passthrough fires.
    if (layoutsEqual(layout, newLayout)) return;
    onLayoutChange(newLayout);
  }, [onLayoutChange, layout]);

  return (
    <GridLayoutWithWidth
      className={className}
      style={style}
      layout={layout}
      onLayoutChange={handleLayoutChange}
      onDragStart={handleDragStart}
      onDragStop={handleDragStop}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
      cols={cols}
      rowHeight={rowHeight}
      margin={margin}
      containerPadding={containerPadding}
      compactType={compactType}
      preventCollision={preventCollision}
      isDraggable={isDraggable}
      isResizable={isResizable}
      draggableCancel=".highcharts-legend, .highcharts-legend *"
    >
      {layout.map((item) => (
        <div key={item.i} className="grid-item-wrapper" data-grid-id={item.i}>
          {renderItem(item.i)}
        </div>
      ))}
    </GridLayoutWithWidth>
  );
}
